import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import ImportDryRun from "@/components/admin/ImportDryRun";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";


type Result = {
  filename: string;
  export_kind: "blank_template" | "current_data";
  workbook_instance_id: string;
  schema_version: string;
  exported_at: string;
  exported_athlete_count: number;
  workbook_sha256: string;
  xlsx_base64: string;
};

const downloadBase64 = (base64: string, filename: string) => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(
    new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const AdminBulkImport = () => {
  const [busy, setBusy] = useState<"blank_template" | "current_data" | null>(null);
  const [last, setLast] = useState<Result | null>(null);

  const generate = async (kind: "blank_template" | "current_data") => {
    setBusy(kind);
    try {
      const { data, error } = await supabase.functions.invoke<Result>("generate-workbook", {
        body: { kind },
      });
      if (error) throw error;
      if (!data?.xlsx_base64) throw new Error("No workbook returned");
      downloadBase64(data.xlsx_base64, data.filename);
      setLast(data);
      toast.success(
        kind === "current_data"
          ? `Downloaded ${data.exported_athlete_count} athlete records`
          : "Blank template downloaded",
      );
    } catch (err: any) {
      toast.error(err?.message ?? "Could not generate the workbook");
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <h1 className="font-display text-4xl">Bulk athlete import</h1>
        <p className="mt-2 text-muted-foreground">
          Download a spreadsheet to prepare a batch of athlete profiles, then upload it to see
          exactly what it would do. Nothing changes until you download a backup and confirm.
        </p>


        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="bg-card rounded-[1.5rem] shadow-card p-6">
            <h2 className="font-display text-2xl">Blank template</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              An empty workbook for adding brand-new athletes. Every sheet, heading and dropdown is
              ready to fill in.
            </p>
            <button
              onClick={() => generate("blank_template")}
              disabled={busy !== null}
              className="mt-5 w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
            >
              {busy === "blank_template" ? "Preparing…" : "Download blank template"}
            </button>
          </div>

          <div className="bg-card rounded-[1.5rem] shadow-card p-6">
            <h2 className="font-display text-2xl">Current athlete data</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Every athlete on file today, with their photos already linked, so you can edit and
              hand it back without re-uploading images.
            </p>
            <button
              onClick={() => generate("current_data")}
              disabled={busy !== null}
              className="mt-5 w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
            >
              {busy === "current_data" ? "Preparing…" : "Download current data"}
            </button>
          </div>
        </div>

        {last && (
          <div className="mt-8 bg-muted rounded-[1.5rem] p-6 text-sm">
            <h3 className="font-semibold">Last download</h3>
            <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-[auto,1fr]">
              <dt className="text-muted-foreground">File</dt>
              <dd className="font-mono break-all">{last.filename}</dd>
              <dt className="text-muted-foreground">Kind</dt>
              <dd>{last.export_kind === "current_data" ? "Current data" : "Blank template"}</dd>
              <dt className="text-muted-foreground">Athlete records included</dt>
              <dd>{last.exported_athlete_count}</dd>
              <dt className="text-muted-foreground">Workbook identity</dt>
              <dd className="font-mono break-all">{last.workbook_instance_id}</dd>
            </dl>
            <p className="mt-3 text-muted-foreground">
              Each download carries its own identity, so a workbook can only ever be applied once.
              Copies and edited copies keep that same identity.
            </p>
          </div>
        )}

        <ImportDryRun />
      </div>

    </AdminLayout>
  );
};

export default AdminBulkImport;
